import * as React from 'react'
import { createContext, Dispatch, SetStateAction, useState } from 'react'
import { login as apiLogin } from './user/api'
import { can, canAny, hasSubscription, isWebsitePlan, User, user } from './user/user'
import { camelizeKeys } from 'humps'
import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query'
import { claims, LoginResponse } from './user/login'
import { AuthenticateInput } from './user/input/authenticate-input'
import i18n from 'i18next'
import { Locale } from './user/locale'
import { Permission } from './user/permission'
import { Company } from './company/company'
import { attributesVisible } from './components/candidate-attributes-toggle'
import { timestampVisible } from './components/candidate-timestamp-toggle'
import { phoneVisible } from './components/candidate-phone-toggle'

export interface AuthContext {
    isAuthenticated: boolean
    // eslint-disable-next-line
    login: UseMutationResult<any, any, any, any>
    logout: () => void
    user: User | null
    onTrial: boolean
    activeCompany: Company | null
    can: (permission: Permission | Permission[]) => boolean
    canAny: (permission: Permission[]) => boolean
    is: (userToCheck: Pick<User, 'id'>) => boolean
    setUser: (user: User) => void
    hasSubscription: boolean
    websitePlan: boolean
    isImpersonating: boolean
    isAdmin: boolean
    loginFromResponse: (response: LoginResponse) => void
    stopImpersonation: () => void
    showCandidateAttributes: boolean
    setShowCandidateAttributes: Dispatch<SetStateAction<boolean>>
    showCandidateTimestamp: boolean
    setShowCandidateTimestamp: Dispatch<SetStateAction<boolean>>
    showCandidatePhone: boolean
    setShowCandidatePhone: Dispatch<SetStateAction<boolean>>
}

const AuthContext = createContext<AuthContext | null>(null)
const userKey = 'auth.user'

export function tokenClaims(token: string) {
    token = token.replace(/-/g, '+').replace(/_/g, '/')

    while (token.length % 4 !== 0) {
        token += '='
    }

    const claimsData = JSON.parse(atob(token.split('.')[1]))

    return claims.parse(camelizeKeys(claimsData))
}

function getStoredUser() {
    const item = localStorage.getItem(userKey)

    if (item === null) {
        return item
    }

    try {
        return user.parse(JSON.parse(item))
    } catch {
        storeUser(null)

        return null
    }
}

export function storeUser(user: User | null) {
    if (user) {
        localStorage.setItem(userKey, JSON.stringify(user))
    } else {
        localStorage.removeItem(userKey)
    }
}

type TokenType = 'access' | 'refresh' | 'previous.access' | 'previous.refresh'

export function storageKeyForTokenType(type: TokenType) {
    return `auth.${type}.token`
}

export function savePreviousTokens() {
    const previousAccess = getToken('access')
    const previousRefresh = getToken('refresh')

    storeToken(previousAccess, 'previous.access')
    storeToken(previousRefresh, 'previous.refresh')
}

function stopImpersonation() {
    const previousAccess = getToken('previous.access')
    const previousRefresh = getToken('previous.refresh')

    storeToken(previousAccess, 'access')
    storeToken(previousRefresh, 'refresh')
    storeToken(null, 'previous.access')
    storeToken(null, 'previous.refresh')
}

export function getToken(type: TokenType): string | null {
    const tokenValue = localStorage.getItem(storageKeyForTokenType(type))

    if (!tokenValue) {
        return null
    }

    return tokenValue
}

export function storeToken(token: string | null, type: TokenType) {
    if (token) {
        localStorage.setItem(storageKeyForTokenType(type), token)
    } else {
        localStorage.removeItem(storageKeyForTokenType(type))
    }
}

export function isTokenExpired(token: string) {
    const claims = tokenClaims(token)
    return Date.now() >= claims.exp * 1000
}

export function clearStorage() {
    storeToken(null, 'access')
    storeToken(null, 'refresh')
    storeToken(null, 'previous.access')
    storeToken(null, 'previous.refresh')
    storeUser(null)
}

export function defaultLocale() {
    return localStorage.getItem('defaultLocale') as null | Locale
}

export function storeDefaultLocale(locale: Locale) {
    localStorage.setItem('defaultLocale', locale)
}

/**
 * Where to go after signing in or up: only a path inside the app, so a link cannot send people to another site.
 */
export function redirectPath(redirect?: string) {
    return redirect && /^\/(?![\\/])/.test(redirect) ? redirect : '/dashboard'
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const queryClient = useQueryClient()
    const [user, innerSetUser] = React.useState<User | null>(getStoredUser())
    const [showCandidateAttributes, setShowCandidateAttributes] = useState<boolean>(attributesVisible())
    const [showCandidateTimestamp, setShowCandidateTimestamp] = useState<boolean>(timestampVisible())
    const [showCandidatePhone, setShowCandidatePhone] = useState<boolean>(phoneVisible())

    const isAuthenticated = !!user

    const logout = React.useCallback(() => {
        queryClient.removeQueries()
        clearStorage()
        innerSetUser(null)
    }, [queryClient])

    const setUser: AuthContext['setUser'] = (user) => {
        innerSetUser(user)
        i18n.changeLanguage(user.locale)
        storeDefaultLocale(user.locale)
    }

    const loginFromResponse: AuthContext['loginFromResponse'] = (data) => {
        queryClient.clear()
        storeToken(data[0], 'access')
        storeToken(data[1], 'refresh')

        const user = data[2]
        storeUser(user)
        setUser(user)
    }

    const login = useMutation({
        mutationFn: ({ email, password, remember }: AuthenticateInput) => {
            return apiLogin({
                email,
                password,
                remember,
            })
        },
        onSuccess: loginFromResponse,
    })
    const activeCompany = user?.activeCompany || null
    const trialEndsAt = activeCompany?.trialEndsAt ?? null

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated,
                user,
                login,
                logout,
                onTrial: trialEndsAt !== null ? trialEndsAt > new Date() : false,
                activeCompany,
                hasSubscription: user?.role === 'admin' || hasSubscription(user?.activeCompany),
                websitePlan: user?.role !== 'admin' && isWebsitePlan(user?.activeCompany),
                stopImpersonation: () => {
                    stopImpersonation()
                    queryClient.invalidateQueries()
                },
                isImpersonating: !!localStorage.getItem(storageKeyForTokenType('previous.access')),
                is: (userToCheck) => {
                    return userToCheck.id === user?.id
                },
                isAdmin: user?.role === 'admin',
                can: (permission) => {
                    if (!user) {
                        return false
                    }
                    return can(user, permission)
                },
                canAny: (permissions) => {
                    if (!user) {
                        return false
                    }

                    return canAny(user, permissions)
                },
                setUser,
                loginFromResponse,
                showCandidateAttributes,
                setShowCandidateAttributes,
                showCandidateTimestamp,
                setShowCandidateTimestamp,
                showCandidatePhone,
                setShowCandidatePhone,
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}

export function useAuth() {
    const context = React.useContext(AuthContext)
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}
